import React, { useState, useEffect } from 'react';
import { Patient, Vitals } from '../types';
import WhoGrowthChart from './WhoGrowthChart';

interface PediatricDevelopmentProps {
  patient: Patient;
  vitals: Vitals[];
  apiToken?: string;
  onRefresh?: () => void;
}

// WHO/CDC standard developmental milestones
const resourceMilestones = [
  {
    ageMonths: 2,
    normalRange: "1.5 - 3 months / من شهر ونصف إلى ٣ أشهر",
    gross: ["Tries to look at parent / يحاول النظر لوجه الوالدين", "Calms down when spoken to / يهدأ عند التحدث إليه"],
    fine: ["Keeps hands in a fist / يبقي يديه على شكل قبضة", "Follows thing with eyes / يتتبع الأشياء بعينيه"],
    speech: ["Makes cooing/gurgling sounds / يصدر أصوات المكاواة", "Turns head towards sounds / يدير رأسه نحو الأصوات"]
  },
  {
    ageMonths: 4,
    normalRange: "3 - 5 months / من ٣ إلى ٥ أشهر",
    gross: ["Holds head steady unsupported / يثبت رأسه دون دعم", "Pushes up on elbows when on tummy / يرتفع على مرفقيه عند النوم على البطن"],
    fine: ["Brings hands to mouth / يجلب يديه إلى فمه", "Holds a toy and shakes it / يمسك باللعبة ويهزها"],
    speech: ["Babbles with expression / يناغي بتعبيرات واضحة", "Copies sounds heard / يقلد الأصوات التي يسمعها"]
  },
  {
    ageMonths: 6,
    normalRange: "5 - 7 months / من ٥ إلى ٧ أشهر",
    gross: ["Rolls over from tummy to back / يتقلب من البطن إلى الظهر", "Sits with support / يجلس بالاعتماد على دعم"],
    fine: ["Reaches for toy with one hand / يمد يده للعبة بيد واحدة", "Passes things from one hand to other / ينقل الأشياء من يد إلى أخرى"],
    speech: ["Responds to own name / يستجيب عند سماع اسمه", "Makes joy and displeasure sounds / يصدر أصوات فرح أو ضيق"]
  },
  {
    ageMonths: 9,
    normalRange: "8 - 10 months / من ٨ إلى ١٠ أشهر",
    gross: ["Crawls / يحبو", "Sits without support / يجلس دون دعم", "Pulls up to stand / يقف مستنداً إلى الأثاث"],
    fine: ["Uses thumb and pointer to pick up / يستخدم الإبهام والسبابة للالتقاط", "Has favorite toys / لديه ألعاب مفضلة"],
    speech: ["Understands 'no' / يفهم كلمة 'لا'", "Makes lots of different sounds / يصدر دمجاً مختلفاً من المناغاة"]
  },
  {
    ageMonths: 12,
    normalRange: "11 - 13 months / من ١١ إلى ١٣ شهراً",
    gross: ["Stands alone / يقف بمفرده", "Walks holding on to furniture / يمشي متمسكاً بالأثاث"],
    fine: ["Puts things into/out of container / يدخل ويخرج الأشياء من صندوق", "Pokes with index finger / يشير بإصبع السبابة"],
    speech: ["Says 'mama' and 'dada' / يقول 'ماما' و'بابا'", "Uses simple gestures like waving / يلوح بيده مودعاً"]
  },
  {
    ageMonths: 18,
    normalRange: "14 - 20 months / من ١٤ إلى ٢٠ شهراً",
    gross: ["Walks alone / يمشي بمفرده", "Helps undress / يساعد في خلع ملابسه", "Walks up steps / يصعد الدرج"],
    fine: ["Scribbles on paper / يشخبط على الورق", "Drinks from a cup / يشرب من الكوب بمفرده", "Feeds self with spoon / يأكل بالملعقة"],
    speech: ["Says several single words / ينطق عدة كلمات مفردة", "Points to show someone what they want / يشير ليوضح ما يريد"]
  },
  {
    ageMonths: 24,
    normalRange: "21 - 27 months / من ٢١ إلى ٢٧ شهراً",
    gross: ["Kicks a ball / يركل الكرة", "Runs easily / يجري بسهولة", "Jumps up / يقفز عن الأرض"],
    fine: ["Builds towers of 4+ blocks / يبني برجاً من ٤ مكعبات أو أكثر", "Turns knobs / يدير المقابض"],
    speech: ["Says sentences of 2-4 words / ينطق عبارات من ٢ إلى ٤ كلمات", "Follows 2-step instructions / يتبع تعليمات من خطوتين"]
  },
  {
    ageMonths: 36,
    normalRange: "30 - 40 months / من ٣٠ إلى ٤٠ شهراً",
    gross: ["Climbs well / يتسلق بمهارة", "Pedals a tricycle / يقود الدراجة ثلاثية العجلات"],
    fine: ["Draws a circle / يرسم دائرة", "Turns book pages one by one / يقلب صفحات الكتاب واحدة تلو الأخرى"],
    speech: ["Asks 'who', 'what', 'where' / يسأل 'من'، 'ماذا'، 'أين'", "Speaks clearly enough for strangers / يتحدث بوضوح كافٍ للغرباء"]
  },
  {
    ageMonths: 48,
    normalRange: "42 - 54 months / من ٤٢ إلى ٥٤ شهراً",
    gross: ["Hops and stands on one foot / يقفز ويقف على قدم واحدة لمدة ثوانٍ", "Catches a bounced ball / يمسك الكرة المرتدة"],
    fine: ["Cuts with safety scissors / يقص بمقص الأمان للأطفال", "Draws a person with 3+ parts / يرسم شخصاً بـ ٣ أجزاء على الأقل"],
    speech: ["Tells stories / يروي قصصاً قصيرة ومترابطة", "Sings a song from memory / يغني أغنية من الذاكرة"]
  },
  {
    ageMonths: 60,
    normalRange: "55 - 66 months / من ٥٥ إلى ٦٦ شهراً",
    gross: ["Swings and climbs / يترجح ويتسلق بمهارة عالية", "Skips on alternate feet / يقفز برجليه بالتناوب"],
    fine: ["Uses fork and spoon / يستخدم الشوكة والملعقة ببراعة", "Copies triangles/shapes / ينقل المثلث أو الأشكال الهندسية"],
    speech: ["Speaks very clearly / يتكلم بوضوح تام", "Uses future tense (e.g. 'I will go') / يستخدم صيغة المستقبل"]
  }
];

// Helper to look up nearest WHO weight-for-age percentile statistics
const lookupWhoReference = (isGirl: boolean, months: number) => {
  const data = isGirl ? [
    { month: 0, p3: 2.4, p15: 2.8, p50: 3.2, p85: 3.7, p97: 4.2 },
    { month: 6, p3: 5.8, p15: 6.5, p50: 7.3, p85: 8.2, p97: 9.3 },
    { month: 12, p3: 7.0, p15: 7.9, p50: 8.9, p85: 10.2, p97: 11.5 },
    { month: 18, p3: 8.1, p15: 9.1, p50: 10.2, p85: 11.6, p97: 13.2 },
    { month: 24, p3: 9.0, p15: 10.2, p50: 11.5, p85: 13.1, p97: 14.8 },
    { month: 30, p3: 10.0, p15: 11.2, p50: 12.7, p85: 14.6, p97: 16.5 },
    { month: 36, p3: 10.8, p15: 12.2, p50: 13.9, p85: 16.0, p97: 18.1 },
    { month: 42, p3: 11.6, p15: 13.2, p50: 15.0, p85: 17.4, p97: 19.8 },
    { month: 48, p3: 12.3, p15: 14.2, p50: 16.1, p85: 18.8, p97: 21.5 },
    { month: 54, p3: 13.0, p15: 15.1, p50: 17.2, p85: 20.2, p97: 23.2 },
    { month: 60, p3: 13.7, p15: 16.0, p50: 18.2, p85: 21.6, p97: 24.9 },
  ] : [
    { month: 0, p3: 2.5, p15: 2.9, p50: 3.3, p85: 3.9, p97: 4.4 },
    { month: 6, p3: 6.4, p15: 7.1, p50: 7.9, p85: 8.9, p97: 9.8 },
    { month: 12, p3: 7.7, p15: 8.6, p50: 9.6, p85: 10.8, p97: 12.0 },
    { month: 18, p3: 8.8, p15: 9.8, p50: 10.9, p85: 12.2, p97: 13.7 },
    { month: 24, p3: 9.7, p15: 10.8, p50: 12.2, p85: 13.7, p97: 15.3 },
    { month: 30, p3: 10.5, p15: 11.8, p50: 13.3, p85: 15.0, p97: 16.9 },
    { month: 36, p3: 11.3, p15: 12.7, p50: 14.3, p85: 16.2, p97: 18.3 },
    { month: 42, p3: 12.0, p15: 13.6, p50: 15.3, p85: 17.4, p97: 19.7 },
    { month: 48, p3: 12.7, p15: 14.4, p50: 16.3, p85: 18.6, p97: 21.2 },
    { month: 54, p3: 13.4, p15: 15.3, p50: 17.3, p85: 19.8, p97: 22.7 },
    { month: 60, p3: 14.1, p15: 16.2, p50: 18.3, p85: 21.0, p97: 24.2 },
  ];

  let closest = data[0];
  let minDiff = Math.abs(data[0].month - months);
  for (const item of data) {
    const diff = Math.abs(item.month - months);
    if (diff < minDiff) {
      minDiff = diff;
      closest = item;
    }
  }
  return closest;
};

// Helper to look up nearest WHO Head Circumference percentile statistics (cm)
const lookupWhoHcReference = (isGirl: boolean, months: number) => {
  const data = isGirl ? [
    { month: 0, p3: 32.7, p15: 33.3, p50: 33.9, p85: 34.5, p97: 35.1 },
    { month: 6, p3: 40.0, p15: 41.1, p50: 42.2, p85: 43.3, p97: 44.4 },
    { month: 12, p3: 42.8, p15: 43.8, p50: 44.9, p85: 46.0, p97: 47.0 },
    { month: 18, p3: 44.1, p15: 45.1, p50: 46.2, p85: 47.3, p97: 48.4 },
    { month: 24, p3: 45.1, p15: 46.1, p50: 47.2, p85: 48.3, p97: 49.3 },
    { month: 30, p3: 45.8, p15: 46.8, p50: 47.9, p85: 49.0, p97: 50.0 },
    { month: 36, p3: 46.4, p15: 47.4, p50: 48.5, p85: 49.6, p97: 50.6 },
    { month: 42, p3: 46.8, p15: 47.9, p50: 49.0, p85: 50.1, p97: 51.1 },
    { month: 48, p3: 47.3, p15: 48.3, p50: 49.5, p85: 50.6, p97: 51.7 },
    { month: 54, p3: 47.7, p15: 48.7, p50: 49.9, p85: 51.0, p97: 52.1 },
    { month: 60, p3: 48.0, p15: 49.1, p50: 50.3, p85: 51.5, p97: 52.6 },
  ] : [
    { month: 0, p3: 33.1, p15: 33.8, p50: 34.5, p85: 35.2, p97: 35.9 },
    { month: 6, p3: 41.2, p15: 42.2, p50: 43.3, p85: 44.4, p97: 45.4 },
    { month: 12, p3: 44.0, p15: 45.0, p50: 46.1, p85: 47.2, p97: 48.2 },
    { month: 18, p3: 45.4, p15: 46.4, p50: 47.4, p85: 48.5, p97: 49.6 },
    { month: 24, p3: 46.3, p15: 47.3, p50: 48.3, p85: 49.3, p97: 50.4 },
    { month: 30, p3: 47.0, p15: 48.0, p50: 49.0, p85: 50.1, p97: 51.1 },
    { month: 36, p3: 47.5, p15: 48.5, p50: 49.5, p85: 50.6, p97: 51.6 },
    { month: 42, p3: 48.0, p15: 49.0, p50: 50.0, p85: 51.1, p97: 52.1 },
    { month: 48, p3: 48.3, p15: 49.3, p50: 50.4, p85: 51.5, p97: 52.5 },
    { month: 54, p3: 48.7, p15: 49.6, p50: 50.8, p85: 51.9, p97: 52.9 },
    { month: 60, p3: 48.9, p15: 49.9, p50: 51.0, p85: 52.1, p97: 53.2 },
  ];

  let closest = data[0];
  let minDiff = Math.abs(data[0].month - months);
  for (const item of data) {
    const diff = Math.abs(item.month - months);
    if (diff < minDiff) {
      minDiff = diff;
      closest = item;
    }
  }
  return closest;
};

const iraqVaccinationSchedule = [
  {
    ageMonths: 0,
    ageLabel: "عند الولادة (Birth)",
    vaccines: [
      { id: "bcg", name: "BCG (التدرن)" },
      { id: "opv0", name: "OPV0 (شلل الأطفال الفموي)" },
      { id: "hepb1", name: "HepB1 (الكبد الفيروسي ب)" },
    ]
  },
  {
    ageMonths: 2,
    ageLabel: "عمر شهرين (2 Months)",
    vaccines: [
      { id: "hexa1", name: "اللقاح السداسي (Hexavalent)" },
      { id: "opv1", name: "OPV1 (شلل الأطفال)" },
      { id: "rota1", name: "Rota1 (الروتا)" },
      { id: "pcv1", name: "PCV1 (المكورات الرئوية)" },
    ]
  },
  {
    ageMonths: 4,
    ageLabel: "عمر 4 أشهر (4 Months)",
    vaccines: [
      { id: "hexa2", name: "اللقاح السداسي (Hexavalent)" },
      { id: "opv2", name: "OPV2" },
      { id: "rota2", name: "Rota2" },
      { id: "pcv2", name: "PCV2" },
    ]
  },
  {
    ageMonths: 6,
    ageLabel: "عمر 6 أشهر (6 Months)",
    vaccines: [
      { id: "hexa3", name: "اللقاح السداسي (Hexavalent)" },
      { id: "opv3", name: "OPV3" },
      { id: "rota3", name: "Rota3" },
      { id: "pcv3", name: "PCV3" },
    ]
  },
  {
    ageMonths: 9,
    ageLabel: "عمر 9 أشهر (9 Months)",
    vaccines: [
      { id: "measles", name: "Measles (الحصبة المنفردة)" },
      { id: "vita1", name: "فيتامين أ (Vitamin A)" },
    ]
  },
  {
    ageMonths: 15,
    ageLabel: "عمر 15 شهراً (15 Months)",
    vaccines: [
      { id: "mmr1", name: "MMR1 (الحصبة والنكاف والحصبة الألمانية)" },
    ]
  },
  {
    ageMonths: 18,
    ageLabel: "عمر 18 شهراً (18 Months)",
    vaccines: [
      { id: "dtp_boost1", name: "DTP Booster 1 (الجرعة المنشطة الأولى)" },
      { id: "opv_boost1", name: "OPV Booster 1" },
      { id: "mmr2", name: "MMR2" },
      { id: "vita2", name: "فيتامين أ (Vitamin A)" },
    ]
  },
  {
    ageMonths: 48,
    ageLabel: "عمر 4-6 سنوات (4-6 Years)",
    vaccines: [
      { id: "dtp_boost2", name: "DTP Booster 2 (الجرعة المنشطة الثانية)" },
      { id: "opv_boost2", name: "OPV Booster 2" },
    ]
  }
];

const PediatricDevelopment: React.FC<PediatricDevelopmentProps> = ({ 
  patient, 
  vitals, 
  apiToken, 
  onRefresh 
}) => {
  const [activeTab, setActiveTab] = useState<'chart' | 'milestones' | 'vaccines'>('chart');
  
  // Growth entry state
  const [newWeight, setNewWeight] = useState('');
  const [newHeight, setNewHeight] = useState('');
  const [newHeadCircumference, setNewHeadCircumference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logSuccess, setLogSuccess] = useState('');

  // Selected age milestone for view
  const [selectedMilestoneAge, setSelectedMilestoneAge] = useState<number>(12);

  // States for checked milestones and vaccines
  const [checkedMilestones, setCheckedMilestones] = useState<Record<string, boolean>>({});
  const [completedVaccines, setCompletedVaccines] = useState<Record<string, string>>({});
  const [clinicalRemarks, setClinicalRemarks] = useState('');

  if (!patient.dob) return null;
  const ageMonths = (new Date().getTime() - new Date(patient.dob).getTime()) / (1000 * 60 * 60 * 24 * 30.4375);
  const isGirl = patient.gender?.toUpperCase() === 'FEMALE';
  const whoRef = lookupWhoReference(isGirl, ageMonths);
  const whoHcRef = lookupWhoHcReference(isGirl, ageMonths);

  // Set initial selected milestone age based on actual estimated age category
  useEffect(() => {
    const currentAge = Math.floor(ageMonths);
    const closestAgeObj = resourceMilestones.reduce((prev, curr) => {
      return Math.abs(curr.ageMonths - currentAge) < Math.abs(prev.ageMonths - currentAge) ? curr : prev;
    });
    setSelectedMilestoneAge(closestAgeObj.ageMonths);
  }, [ageMonths]);

  // Load persistence for checked milestones and clinical notes
  useEffect(() => {
    try {
      const savedChecked = localStorage.getItem(`ped_milestones_${patient.id}`);
      if (savedChecked) {
        setCheckedMilestones(JSON.parse(savedChecked));
      } else {
        setCheckedMilestones({});
      }
      const savedVaccines = localStorage.getItem(`ped_vaccines_${patient.id}`);
      if (savedVaccines) {
        setCompletedVaccines(JSON.parse(savedVaccines));
      } else {
        setCompletedVaccines({});
      }
      const savedRemarks = localStorage.getItem(`ped_remarks_${patient.id}`) || '';
      setClinicalRemarks(savedRemarks);
    } catch (e) {
      console.error(e);
    }
  }, [patient.id]);

  const handleToggleMilestone = (key: string) => {
    const updated = { ...checkedMilestones, [key]: !checkedMilestones[key] };
    setCheckedMilestones(updated);
    localStorage.setItem(`ped_milestones_${patient.id}`, JSON.stringify(updated));
  };

  const handleToggleVaccine = (vaccineId: string) => {
    const updated = { ...completedVaccines };
    if (updated[vaccineId]) {
      delete updated[vaccineId];
    } else {
      updated[vaccineId] = new Date().toISOString().split('T')[0];
    }
    setCompletedVaccines(updated);
    localStorage.setItem(`ped_vaccines_${patient.id}`, JSON.stringify(updated));
  };

  const handleSaveRemarks = (val: string) => {
    setClinicalRemarks(val);
    localStorage.setItem(`ped_remarks_${patient.id}`, val);
  };

  // Easily add record to patient growth chart
  const handleAddGrowthRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWeight || isNaN(parseFloat(newWeight))) {
      alert("Please enter a valid weight parameter.");
      return;
    }

    setIsSubmitting(true);
    try {
      const weightVal = parseFloat(newWeight);
      const heightVal = newHeight ? parseFloat(newHeight) : undefined;
      const hcVal = newHeadCircumference ? parseFloat(newHeadCircumference) : undefined;
      let bmiVal: number | undefined;
      
      if (weightVal && heightVal) {
        bmiVal = weightVal / ((heightVal / 100) * (heightVal / 100));
      }

      const res = await fetch("/api/vitals", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          patientId: patient.id,
          weight: weightVal,
          height: heightVal,
          headCircumference: hcVal,
          bmi: bmiVal ? parseFloat(bmiVal.toFixed(2)) : undefined,
          recordedBy: "Pediatric Specialist / طبيب الأطفال",
          chiefComplaint: "Routine Pediatric Growth & Development Assessment"
        })
      });

      if (res.ok) {
        setLogSuccess("Growth coordinates registered successfully!");
        setNewWeight('');
        setNewHeight('');
        setNewHeadCircumference('');
        if (onRefresh) {
          onRefresh();
        }
        setTimeout(() => setLogSuccess(''), 4000);
      } else {
        throw new Error("Failed to save pediatric record to clinical database.");
      }
    } catch (err) {
      console.error(err);
      alert("Error logging growth parameters. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMilestone = resourceMilestones.find(m => m.ageMonths === selectedMilestoneAge) || resourceMilestones[4];

  return (
    <div className="bg-white border text-sm border-slate-200 rounded-lg shadow-sm w-full p-5 space-y-5">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-3 gap-3">
        <div>
          <h3 className="font-extrabold text-[#1E293B] text-sm flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
            WHO Comprehensive Pediatric Assessment (0-5 Years) / التقييم وقياسات النمو الشاملة للأطفال
          </h3>
          <p className="text-xs text-slate-500 font-medium">
            Patient: <span className="font-semibold text-slate-700">{patient.name}</span> • Current Age: <span className="font-semibold text-indigo-700 font-mono">{Math.floor(ageMonths)} Months</span> ({isGirl ? "Female / أنثى" : "Male / ذكر"})
          </p>
        </div>
        
        <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
          <button 
            type="button"
            onClick={() => setActiveTab('chart')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-150 ${activeTab === 'chart' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-800'}`}
          >
            📉 Growth Chart & Logs
          </button>
          <button 
            type="button"
            onClick={() => setActiveTab('milestones')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-150 ${activeTab === 'milestones' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-800'}`}
          >
            🧠 Developmental Milestones (WHO)
          </button>
          <button 
            type="button"
            onClick={() => setActiveTab('vaccines')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all duration-150 ${activeTab === 'vaccines' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-800'}`}
          >
            💉 Immunization (Vaccines)
          </button>
        </div>
      </div>
      
      <div className="flex flex-col xl:flex-row gap-4">
        {/* Dynamic WHO normal weight reference information display bar */}
        <div className="bg-indigo-50/50 border border-indigo-100/70 rounded-xl p-3.5 flex flex-col md:flex-row justify-between gap-4 items-start sm:items-center flex-1">
          <div>
            <div className="text-[10px] font-bold text-indigo-900 uppercase tracking-wider mb-0.5">WHO Weight Lookup / معايير نمو الوزن</div>
            <div className="text-[10px] text-slate-700 font-medium font-sans">
              Targets for <span className="text-indigo-700 font-bold">{Math.floor(ageMonths)} months</span> {isGirl ? "girl" : "boy"}:
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full md:w-auto text-xs font-semibold">
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100">
              <span className="text-slate-500 text-[9px] block font-medium">Median</span>
              <span className="text-emerald-700 font-mono font-bold text-xs">{whoRef.p50} kg</span>
            </div>
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100">
              <span className="text-slate-500 text-[9px] block font-medium">Range (P15-P85)</span>
              <span className="text-orange-600 font-mono font-bold text-xs">{whoRef.p15} - {whoRef.p85} kg</span>
            </div>
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100 col-span-2 sm:col-span-1 border-rose-100">
              <span className="text-slate-500 text-[9px] block font-medium">Alert</span>
              <span className="text-rose-600 font-mono font-bold text-xs">&lt;{whoRef.p3} or &gt;{whoRef.p97} kg</span>
            </div>
          </div>
        </div>

        {/* Dynamic WHO normal head circumference reference display bar */}
        <div className="bg-emerald-50/50 border border-emerald-100/70 rounded-xl p-3.5 flex flex-col md:flex-row justify-between gap-4 items-start sm:items-center flex-1">
          <div>
            <div className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider mb-0.5">WHO Head Circ. / معايير محيط الرأس</div>
            <div className="text-[10px] text-slate-700 font-medium font-sans">
              Targets for <span className="text-emerald-700 font-bold">{Math.floor(ageMonths)} months</span> {isGirl ? "girl" : "boy"}:
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 w-full md:w-auto text-xs font-semibold">
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100">
              <span className="text-slate-500 text-[9px] block font-medium">Median</span>
              <span className="text-emerald-700 font-mono font-bold text-xs">{whoHcRef.p50} cm</span>
            </div>
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100">
              <span className="text-slate-500 text-[9px] block font-medium">Range (P15-P85)</span>
              <span className="text-orange-600 font-mono font-bold text-xs">{whoHcRef.p15} - {whoHcRef.p85} cm</span>
            </div>
            <div className="bg-white px-2 py-1.5 rounded-md border border-slate-100 col-span-2 sm:col-span-1 border-rose-100">
              <span className="text-slate-500 text-[9px] block font-medium">Alert</span>
              <span className="text-rose-600 font-mono font-bold text-xs">&lt;{whoHcRef.p3} or &gt;{whoHcRef.p97} cm</span>
            </div>
          </div>
        </div>
      </div>

      {activeTab === 'chart' && (
        <div className="space-y-5">
          {/* Growth Chart View */}
          <WhoGrowthChart patient={patient} vitals={vitals} />
          
          {/* Quick Add growth records form */}
          <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4">
            <h4 className="font-extrabold text-slate-800 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <span>🩺 Add New Growth Record / تسجيل قياس تطور الوزن والجهد الجديد</span>
            </h4>
            
            <form onSubmit={handleAddGrowthRecord} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">Weight / الوزن (kg) *</label>
                <input 
                  type="number" 
                  step="0.01"
                  required 
                  value={newWeight}
                  onChange={e => setNewWeight(e.target.value)}
                  placeholder="e.g. 12.5" 
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">Height / الطول (cm) <span className="text-slate-400 font-normal">(Optional)</span></label>
                <input 
                  type="number" 
                  step="0.1"
                  value={newHeight}
                  onChange={e => setNewHeight(e.target.value)}
                  placeholder="e.g. 85.0" 
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">Head Circ. (cm) <span className="text-slate-400 font-normal">(WHO &lt;5y)</span></label>
                <input 
                  type="number" 
                  step="0.1"
                  value={newHeadCircumference}
                  onChange={e => setNewHeadCircumference(e.target.value)}
                  placeholder="e.g. 45.0" 
                  className="w-full bg-white border border-slate-300 rounded-lg p-2 text-xs font-mono outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold py-2 px-4 rounded-lg text-xs shadow-sm transition cursor-pointer flex items-center justify-center gap-2 h-9"
                >
                  {isSubmitting ? "Saving..." : "💾 Save Record"}
                </button>
              </div>
            </form>
            {logSuccess && (
              <div className="mt-2 text-xs text-emerald-600 font-bold bg-emerald-50 px-3 py-1 rounded border border-emerald-100 flex items-center gap-1.5">
                <span>✓</span> {logSuccess}
              </div>
            )}
          </div>
        </div>
      )}
      
      {activeTab === 'milestones' && (
        <div className="space-y-4">
          {/* Developmental Milestones interactive selector */}
          <div className="flex flex-wrap gap-1.5 bg-slate-50 p-2 rounded-lg border border-slate-200">
            {resourceMilestones.map((m) => {
              const matchesAge = m.ageMonths <= ageMonths;
              return (
                <button
                  key={m.ageMonths}
                  type="button"
                  onClick={() => setSelectedMilestoneAge(m.ageMonths)}
                  className={`px-3 py-1.5 rounded text-[11px] font-bold uppercase transition flex items-center gap-1 ${
                    selectedMilestoneAge === m.ageMonths 
                      ? 'bg-indigo-600 text-white shadow-xs' 
                      : matchesAge 
                        ? 'bg-white text-indigo-700 hover:bg-slate-100 border border-slate-200' 
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed hover:bg-slate-200'
                  }`}
                >
                  {m.ageMonths < 12 ? `${m.ageMonths} Months` : `${m.ageMonths / 12} Years`}
                  {matchesAge && <span className="text-[9px] text-emerald-500">✓</span>}
                </button>
              );
            })}
          </div>

          <div className="bg-amber-50/50 border border-amber-200/50 rounded-xl p-3 text-xs text-amber-900 font-medium">
            🎯 <strong className="text-amber-950">Normal Range Expected by WHO:</strong> {selectedMilestone.normalRange}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Gross Motor */}
            <div className="bg-slate-50/30 border border-slate-200 rounded-xl p-3.5 space-y-3">
              <h5 className="font-extrabold text-slate-800 text-xs border-b border-slate-100 pb-1.5 flex justify-between items-center text-indigo-900 uppercase tracking-wide">
                <span>🏃‍♂️ Gross Motor Skills</span>
                <span className="text-[10px] text-slate-400 font-normal">الحركة الكبرى</span>
              </h5>
              <div className="space-y-2">
                {selectedMilestone.gross.map((item, idx) => {
                  const itemKey = `gross_${selectedMilestoneAge}_${idx}`;
                  return (
                    <label key={idx} className="flex items-start gap-2.5 cursor-pointer hover:bg-slate-50 p-1 rounded transition">
                      <input 
                        type="checkbox" 
                        checked={!!checkedMilestones[itemKey]} 
                        onChange={() => handleToggleMilestone(itemKey)}
                        className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                      />
                      <span className={`text-[11px] leading-relaxed text-slate-700 ${checkedMilestones[itemKey] ? 'line-through text-slate-400 font-normal' : 'font-semibold'}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Fine Motor */}
            <div className="bg-slate-50/30 border border-slate-200 rounded-xl p-3.5 space-y-3">
              <h5 className="font-extrabold text-slate-800 text-xs border-b border-slate-100 pb-1.5 flex justify-between items-center text-indigo-900 uppercase tracking-wide">
                <span>✍️ Fine Motor Skills</span>
                <span className="text-[10px] text-slate-400 font-normal">الحركة الصغرى والتركيز</span>
              </h5>
              <div className="space-y-2">
                {selectedMilestone.fine.map((item, idx) => {
                  const itemKey = `fine_${selectedMilestoneAge}_${idx}`;
                  return (
                    <label key={idx} className="flex items-start gap-2.5 cursor-pointer hover:bg-slate-50 p-1 rounded transition">
                      <input 
                        type="checkbox" 
                        checked={!!checkedMilestones[itemKey]} 
                        onChange={() => handleToggleMilestone(itemKey)}
                        className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                      />
                      <span className={`text-[11px] leading-relaxed text-slate-700 ${checkedMilestones[itemKey] ? 'line-through text-slate-400 font-normal' : 'font-semibold'}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Speech and Communication */}
            <div className="bg-slate-50/30 border border-slate-200 rounded-xl p-3.5 space-y-3">
              <h5 className="font-extrabold text-slate-800 text-xs border-b border-slate-100 pb-1.5 flex justify-between items-center text-indigo-900 uppercase tracking-wide">
                <span>💬 Speech & Cognition</span>
                <span className="text-[10px] text-slate-400 font-normal">التخاطب والإدراك</span>
              </h5>
              <div className="space-y-2">
                {selectedMilestone.speech.map((item, idx) => {
                  const itemKey = `speech_${selectedMilestoneAge}_${idx}`;
                  return (
                    <label key={idx} className="flex items-start gap-2.5 cursor-pointer hover:bg-slate-50 p-1 rounded transition">
                      <input 
                        type="checkbox" 
                        checked={!!checkedMilestones[itemKey]} 
                        onChange={() => handleToggleMilestone(itemKey)}
                        className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                      />
                      <span className={`text-[11px] leading-relaxed text-slate-700 ${checkedMilestones[itemKey] ? 'line-through text-slate-400 font-normal' : 'font-semibold'}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Clinical assessment free-notes field */}
          <div className="bg-slate-50/40 border border-slate-200 rounded-xl p-4">
            <label className="block text-xs font-bold text-slate-700 mb-1.5">📝 Pediatric Clinical Growth & Milestone Remarks / ملاحظات الفحص الحركي والنمائي للزيارة الحالية</label>
            <textarea
              rows={3}
              value={clinicalRemarks}
              onChange={e => handleSaveRemarks(e.target.value)}
              placeholder="اكتب فحص تطور الطفل، ملاحظات الاستجابة اللفظية أو الحركية الخاصة بالطفل..."
              className="w-full bg-white border border-slate-300 rounded-lg p-3 text-xs font-semibold outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="text-[10px] text-slate-400 mt-1 italic font-mono">Changes log and checked items are automatically synchronized and securely saved to Patient EHR Profile.</div>
          </div>
        </div>
      )}

      {activeTab === 'vaccines' && (
        <div className="space-y-4">
          <div className="bg-sky-50/50 border border-sky-200/50 rounded-xl p-3 text-xs text-sky-900 font-medium">
            🎯 <strong className="text-sky-950">جدول التلقيحات الوطني:</strong> يعتمد هذا الجدول على توصيات وزارة الصحة العراقية ومنظمة الصحة العالمية للأطفال دون سن 5 سنوات.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {iraqVaccinationSchedule.map((group, gIdx) => {
              const isPastDue = ageMonths >= group.ageMonths;
              return (
                <div key={gIdx} className={`bg-slate-50/30 border rounded-xl p-3.5 space-y-3 ${isPastDue ? 'border-sky-200' : 'border-slate-200 opacity-75'}`}>
                  <h5 className="font-extrabold text-slate-800 text-xs border-b border-slate-100 pb-1.5 flex justify-between items-center text-sky-900 uppercase tracking-wide">
                    <span>📅 {group.ageLabel}</span>
                    {isPastDue && <span className="text-[9px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-bold">مستحق / Required</span>}
                  </h5>
                  <div className="space-y-2">
                    {group.vaccines.map((v) => {
                      const isCompleted = !!completedVaccines[v.id];
                      return (
                        <label key={v.id} className="flex items-start justify-between gap-2.5 cursor-pointer hover:bg-slate-50 p-1.5 rounded transition border border-transparent hover:border-slate-100">
                          <div className="flex items-center gap-2.5">
                            <input 
                              type="checkbox" 
                              checked={isCompleted} 
                              onChange={() => handleToggleVaccine(v.id)}
                              className="mt-0.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500 w-4 h-4"
                            />
                            <span className={`text-[11px] leading-relaxed ${isCompleted ? 'text-slate-400 font-normal' : 'text-slate-700 font-semibold'}`}>
                              {v.name}
                            </span>
                          </div>
                          {isCompleted && (
                            <span className="text-[9px] text-emerald-600 font-mono font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                              ✓ {completedVaccines[v.id]}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PediatricDevelopment;
